import eventBus from '@elements/tools/event-bus';
import { trans } from '@elements/translation';
import IconHouse from '@icons/ph_house.svg';
import IconList from '@icons/ph_list.svg';
import IconMagnifyingGlass from '@icons/ph_magnifying-glass.svg';
import IconUserCircle from '@icons/ph_user-circle.svg';
import { CLOSE_LOGIN_MODAL, CLOSE_MOBILE_PANEL, SHOW_LOGIN_MODAL } from '@molecules/modal-constants';
import { URL_HOME, URL_LOGIN } from '@tools/routes/routes';
import { PLUS_MENU_MOBILE, SEARCH_LINK } from '../config.ts';

const selectors = {
  stickyNav: '.nav-mobile',
  sidebarBarMenuItem: '.sliding-side-bar__content .side-bar-menu__item',
  slidingPanel: '.sliding-side-bar',
  closeButton: '.sliding-header__close',
  menuItem: '.nav-mobile__item',
  currentClass: 'nav-mobile--current',
};

interface MenuItem {
  icon?: string;
  iconName?: string;
  event?: string;
  link?: string;
  similarLinks?: string[];
  name?: string;
}

interface State {
  isPanelOpen: boolean;
  openedLogin: boolean;
  currentPath: string;
}

const menuItems: MenuItem[] = [
  {
    name: trans('user_menu.home'),
    link: URL_HOME,
    icon: IconHouse,
    iconName: 'ph_house',
  },
  SEARCH_LINK,
  {
    name: trans('user_menu.login'),
    link: URL_LOGIN,
    icon: IconUserCircle,
    iconName: 'ph_user-circle',
    event: SHOW_LOGIN_MODAL,
  },
  PLUS_MENU_MOBILE,
];

const handleChildElements = (element: Element, action: 'add' | 'remove'): void => {
  element.classList[action](selectors.currentClass);
};

const activeItem = (element: Element | null): void => {
  if (element) {
    handleChildElements(element, 'add');
  }
};

const cleanActiveUrl = (): void => {
  const elements = document.querySelectorAll(selectors.menuItem);
  elements.forEach((element) => {
    handleChildElements(element, 'remove');
  });
};

const isMenuItemActive = (item: MenuItem, currentPath: string): boolean => {
  if (!item) {
    return false;
  }

  const { similarLinks, link } = item;
  const hasMatchingSimilarLink = similarLinks?.some(
    (similarLink) => currentPath.startsWith(similarLink),
  ) || false;

  return hasMatchingSimilarLink || (!!link && currentPath === link);
};

const updateCurrentMenuItem = (state: State): void => {
  const stickyNav = document.querySelector(selectors.stickyNav);
  if (!stickyNav) {
    return;
  }

  menuItems.forEach((item) => {
    const isActive = isMenuItemActive(item, state.currentPath);
    if (!isActive) {
      return;
    }

    const element = document.querySelector(`[data-sticky-mobile-icon='${item.iconName}']`);
    activeItem(element);
  });
};

const updatePanelVisibility = (state: State): void => {
  const slidingPanel = document.querySelector(selectors.slidingPanel);
  if (!slidingPanel) {
    return;
  }

  slidingPanel.classList.toggle('hidden', !state.isPanelOpen);

  if (!state.isPanelOpen) {
    cleanActiveUrl();
  }
};

const handleMenuClick = (menuItem: MenuItem, state: State): void => {
  if (state.openedLogin) {
    eventBus.dispatchEvent(new CustomEvent(CLOSE_LOGIN_MODAL));
  }

  cleanActiveUrl();

  const element = document.querySelector(`[data-sticky-mobile-icon='${menuItem.iconName}']`);
  activeItem(element);

  if (menuItem.iconName === 'ph_list') {
    state.isPanelOpen = !state.isPanelOpen;
    document.body.classList.toggle('no-scroll');
    updatePanelVisibility(state);
    return;
  }

  if (menuItem.event) {
    if (menuItem.event === SHOW_LOGIN_MODAL) {
      state.openedLogin = true;
    }
    eventBus.dispatchEvent(new CustomEvent(menuItem.event));
    return;
  }

  if (menuItem.link && menuItem.link !== state.currentPath) {
    window.location.href = menuItem.link;
  }
};

const handleClosePanel = (state: State): void => {
  state.isPanelOpen = false;
  updatePanelVisibility(state);
  updateCurrentMenuItem(state);
};

const handleSidebarMenuItemClick = (e: Event): void => {
  const target = e.target as HTMLElement;
  const menuTitle = target.closest('.side-bar-menu__title') as HTMLElement;
  if (!menuTitle) {
    return;
  }
  menuTitle.classList.toggle('side-bar-menu__title--opened-mobile');

  const menuItem = menuTitle.closest('.side-bar-menu__item') as HTMLElement;
  const menuLinks = menuItem?.querySelector('.side-bar-submenu__list') as HTMLElement;
  if (!menuLinks) {
    return;
  }

  menuLinks.classList.toggle('open--no-border');
  menuLinks.classList.toggle('hidden');
};

const handleMenuItemClick = (e: Event, state: State): void => {
  const target = e.target as HTMLElement;
  const menuItem = target.closest(selectors.menuItem);
  if (!menuItem) {
    return;
  }

  const { dataset } = (menuItem as HTMLElement);

  const iconName = dataset.stickyMobileIcon;

  let icon;
  switch (iconName) {
  case 'ph_house':
    icon = IconHouse;
    break;
  case 'ph_magnifying-glass':
    icon = IconMagnifyingGlass;
    break;
  case 'ph_user-circle':
    icon = IconUserCircle;
    break;
  case 'ph_list':
    icon = IconList;
    break;
  default:
    icon = undefined;
  }

  handleMenuClick({
    icon,
    iconName,
    event: dataset.event,
    link: dataset.link,
  }, state);
};

const closePanelAndOpenLogin = (state: State): void => {
  handleClosePanel(state);
  handleMenuClick({ icon: IconUserCircle, iconName: 'ph_user-circle', event: SHOW_LOGIN_MODAL }, state);
};

const setupModalListeners = (state: State): void => {
  const handleModalClose = (): void => {
    state.openedLogin = false;
    cleanActiveUrl();
    handleClosePanel(state);
  };

  eventBus.addEventListener(CLOSE_LOGIN_MODAL, handleModalClose);
  eventBus.addEventListener(CLOSE_MOBILE_PANEL, () => {
    handleClosePanel(state);
  });
};

const initNavigation = (): void => {
  const state: State = {
    isPanelOpen: false,
    openedLogin: false,
    currentPath: window.location.pathname,
  };

  const stickyNav = document.querySelector(selectors.stickyNav) as HTMLElement;
  const closeButton = document.querySelector(selectors.closeButton) as HTMLElement;
  const sidebarBarMenuItem = document.querySelectorAll(selectors.sidebarBarMenuItem);
  const loginAnonymousMenuPlus = document.getElementById('side-bar-menu--login');

  stickyNav?.addEventListener('click', (e) => handleMenuItemClick(e, state));
  sidebarBarMenuItem?.forEach((item) => {
    item.addEventListener('click', handleSidebarMenuItemClick);
  });
  closeButton?.addEventListener('click', () => handleClosePanel(state));
  if (loginAnonymousMenuPlus) {
    loginAnonymousMenuPlus.addEventListener('click', () => closePanelAndOpenLogin(state));
  }

  setupModalListeners(state);

  stickyNav?.setAttribute('data-ready', 'true');

  updateCurrentMenuItem(state);
};

export default (): void => {
  initNavigation();
};
